// firebase.js
import { initializeApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';

// ✅ MEJORADO: Usar variables de entorno para mayor seguridad
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// ✅ VALIDACIÓN: Verificar que las variables de entorno estén configuradas
const validateFirebaseConfig = () => {
  const requiredKeys = [
    'REACT_APP_FIREBASE_API_KEY',
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    'REACT_APP_FIREBASE_PROJECT_ID',
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    'REACT_APP_FIREBASE_APP_ID'
  ];

  const missingKeys = requiredKeys.filter(key => !process.env[key]);
  
  if (missingKeys.length > 0) {
    console.error('❌ Variables de entorno de Firebase faltantes:', missingKeys);
    console.error('💡 Copia env.example a .env y configura las variables');
    throw new Error('Configuración de Firebase incompleta');
  }
};

// Validar configuración en desarrollo
if (process.env.NODE_ENV === 'development') {
  validateFirebaseConfig();
}

// Inicializar Firebase
const app = initializeApp(firebaseConfig);

// App Check: protege Cloud Functions y Firestore de tráfico no legítimo (bots, abuso).
// Usamos ReCaptchaEnterpriseProvider porque en Firebase Console está registrado reCAPTCHA Enterprise.
// La clave debe ser la site key de reCAPTCHA Enterprise (no la de v3 estándar).
const appCheckSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
if (typeof window !== 'undefined' && appCheckSiteKey) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(appCheckSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Ratafit] App Check no inicializado:', err?.message || err);
    }
  }
}

// Inicializar servicios
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
const functionsRegion = process.env.REACT_APP_FIREBASE_REGION || 'us-central1';
export const functions = getFunctions(app, functionsRegion);
export const functionsSA = getFunctions(app, 'southamerica-east1'); 
// Mensajería: solo cargar en contextos soportados (evita messaging/unsupported-browser).
// Firebase Messaging requiere: HTTPS (secureContext), Service Workers, IndexedDB y Push API (PushManager).
let messagingInstance = null;
export function canUseMessaging() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  try {
    return Boolean(
      window.isSecureContext &&
      'serviceWorker' in navigator &&
      'indexedDB' in window &&
      'PushManager' in window &&
      'Notification' in window
    );
  } catch (_) {
    return false;
  }
}
/** Obtiene la instancia de Messaging solo si el navegador lo soporta (async para import dinámico). */
export async function getMessagingSafe() {
  if (!canUseMessaging()) return null;
  if (messagingInstance) return messagingInstance;
  try {
    const { getMessaging } = await import('firebase/messaging');
    messagingInstance = getMessaging(app);
    return messagingInstance;
  } catch (err) {
    messagingInstance = null;
    if (process.env.NODE_ENV === 'development') {
      console.info('[Ratafit] FCM no disponible en este navegador:', err?.message || err);
    }
    return null;
  }
}
/** @deprecated Usar getMessagingSafe() para evitar errores en navegadores no soportados. */
export const messaging = messagingInstance;

export default app;